import { FormValues } from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import { ChildFieldFormValue } from '@/ui/components/customFieldGroupControl/schema';
import { CustomFieldType } from '@payaca/custom-fields';
import { DeepPartial, useFormContext } from 'react-hook-form';
import { PathToFieldsetChild } from '../types';

type UseFieldsetChildFieldConfig = {
  fieldPath?: PathToFieldsetChild;
  expectedType: CustomFieldType;
};

type PartialChildField = DeepPartial<ChildFieldFormValue>;
type ChildrenContainer = { children?: (PartialChildField | undefined)[] };

/**
 * Traverses the form's default values to find the initial value for a nested field.
 *
 * For path "children.0" -> searches in defaultValues.children
 * For path "children.0.children.1" -> searches in defaultValues.children[0].children
 */
function getInitialValueFromPath(
  defaultValues: ChildrenContainer | undefined,
  fieldPath: PathToFieldsetChild,
  identifier: string
): PartialChildField | undefined {
  if (!defaultValues?.children) return undefined;

  // Parse path: "children.0.children.1" -> ["children", "0", "children", "1"]
  const segments = fieldPath.split('.');

  // Navigate to parent container
  // Process all segments except the last two (which are "children.N" pointing to the field itself)
  let container: ChildrenContainer | undefined = defaultValues;

  for (let i = 0; i < segments.length - 2; i += 2) {
    const children = container?.children;
    const index = parseInt(segments[i + 1], 10);
    container = children?.[index] as ChildrenContainer | undefined;
  }

  // Search in the final children array for matching identifier
  return container?.children?.find(
    (child) => child?.identification?.identifier === identifier
  );
}

export function useFieldsetChildField(config: UseFieldsetChildFieldConfig) {
  const { control, watch, formState, getValues } = useFormContext<FormValues>();

  const namePrefix = config.fieldPath || '';

  if (!namePrefix) return null;

  const type = watch(`${namePrefix}.type`);
  const identifier = getValues(`${namePrefix}.identification.identifier`);

  const initialValue = getInitialValueFromPath(
    formState.defaultValues,
    namePrefix,
    identifier
  );

  if (type !== config.expectedType) return null;

  return {
    namePrefix,
    control,
    identifier,
    initialValue,
  };
}
